import React from "react";
import { noop } from "lodash";

import { Component } from "../../../../commons/types/component";
import * as icons from "../../../../resources/icons";
import cn from "../../libs/class-name";
import useDoubleClick from "../../libs/hooks/use-double-click";
import Icon from "../Icon/Icon";
import IconButton from "../IconButton/IconButton";
import Image from "../Image/Image";
import TileIconButton from "../TileIconButton/TileIconButton";

interface Props extends Component {
  image: React.ReactElement<React.ComponentProps<typeof Image>>;
  year?: string;
  cornerAction?: React.ReactNode;
  centerAction?: React.ReactNode;
  href?: string;
  height?: "default" | "l" | "xl";
  badge?: React.ReactElement;
  disabled?: boolean;
  isBackside?: boolean;
  isCloseable?: boolean;
  isFlippable?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>, href: string) => void;
  onDoubleClick?: (event: React.MouseEvent<HTMLElement>, href: string) => void;
  onClose?: () => void;
  onFlip?: () => void;
}

const BrandTile = ({
  classNames = [],
  href = "#",
  image,
  year = "",
  cornerAction = null,
  centerAction = null,
  height = "default",
  badge,
  disabled = false,
  isBackside = false,
  isCloseable = false,
  isFlippable = false,
  onClick = noop,
  onClose = noop,
  onFlip = noop,
  onDoubleClick = noop
}: Props) => {
  const {
    doubleClicked,
    waitingForDoubleClick,
    onBlur: onBlurInner,
    onClick: onClickInner
  } = useDoubleClick(
    event => {
      event.preventDefault();
      onDoubleClick(event, href);
    },
    event => {
      event.preventDefault();
      onClick(event, href);
    }
  );

  return (
    <div
      className={cn(
        "BrandTile",
        [
          {
            touched: (waitingForDoubleClick || doubleClicked) && !centerAction,
            isBackside,
            disabled,
            withCenterAction: !!centerAction,
            [`height-${height}`]: height
          }
        ],
        classNames
      )}
    >
      <div role="button" className="BrandTile__content" onClick={disabled ? noop : onClickInner} onBlur={onBlurInner}>
        <div className="BrandTile__image">
          {React.cloneElement(image, { fill: true, position: "center", timeout: 30_000 })}
        </div>
        {!centerAction && !!year && <div className="BrandTile__year">{year}</div>}
        {centerAction && <div className="BrandTile__centerAction">{centerAction}</div>}
      </div>
      {isCloseable && (
        <div className="BrandTile__closeButton">
          <TileIconButton icon={<Icon source={icons.IconSmallCross} />} onClick={onClose} />
        </div>
      )}
      {isFlippable && !centerAction && (
        <div className="BrandTile__cornerAction">
          <IconButton icon={<Icon source={icons.IconSmallSwitch} />} onClick={onFlip} />
        </div>
      )}
      {!!badge && <div className="BrandTile__badge">{badge}</div>}
      {cornerAction && !centerAction && <div className="BrandTile__cornerAction">{cornerAction}</div>}
    </div>
  );
};

export default BrandTile;
